import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { DecisionQueue } from "./DecisionQueue";
// ReactNode is used by both component props and NavIcon's icon map.
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import {
  customerWorkspaces,
  demoReviewJob,
  estesDefaultShipment,
  estesSampleContext,
  evidenceRows,
  fallbackReviewShipments,
  gates,
  metrics,
  notificationSendConfirm,
  ottoAdminBaseUrl,
  ottoStatusUrl,
  profileVars,
  reviewSubmitConfirm,
  routeSteps,
  type AiReviewFinding,
  type AiReviewResponse,
  type AiReviewSource,
  type CustomerWorkspace,
  type EstesForwardersResponse,
  type EstesNormalizeResponse,
  type EstesShipmentInput,
  type OttoPublicStatus,
  type OttoOperatorAlert,
  type OttoNotificationPreview,
  type OttoNotificationSettings,
  type ReviewShipmentJob,
  type ReviewShipmentRow,
  type SeedTestShipmentResponse,
  type ShipmentPreviewResponse,
  type ShipmentSubmitResponse,
  type SourceReviewResponse,
  type StatusTone
} from "./data";
import { bootstrapOttoClient, type OttoPublicConfig } from "./supabaseClient";

const editableFields: Array<{ key: keyof ReviewShipmentJob; label: string; span?: "wide" }> = [
  { key: "poNumber", label: "PO / BOL" },
  { key: "workOrderId", label: "Work Order" },
  { key: "keyMeId", label: "KID" },
  { key: "task", label: "Action" },
  { key: "suggestedServiceDate", label: "Service Date" },
  { key: "weekEndingDate", label: "Week Ending (WE)" },
  { key: "serialNumber", label: "Serial" },
  { key: "siteName", label: "Property", span: "wide" },
  { key: "address1", label: "Address", span: "wide" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "Zip" },
  { key: "storeContact", label: "Store Contact" },
  { key: "storePhone", label: "Store Phone" },
  { key: "jobNotes", label: "Deployment Notes", span: "wide" }
];

function App() {
  const [bootConfig, setBootConfig] = useState<OttoPublicConfig | null>(null);
  const [supabase, setSupabase] = useState<SupabaseClient | null>(null);
  const [bootError, setBootError] = useState("");
  const [bootLoading, setBootLoading] = useState(true);

  const supabaseConfigured = Boolean(supabase);

  const [status, setStatus] = useState<OttoPublicStatus | null>(null);
  const [statusError, setStatusError] = useState("");
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authStatus, setAuthStatus] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authMode, setAuthMode] = useState<"magic" | "password">("password");
  const [reviewShipments, setReviewShipments] = useState<ReviewShipmentRow[]>([]);
  const [selectedShipmentId, setSelectedShipmentId] = useState(fallbackReviewShipments[0]?.id ?? "");
  const [reviewForm, setReviewForm] = useState<ReviewShipmentJob>(demoReviewJob);
  const [reviewStatus, setReviewStatus] = useState("");
  const [reviewError, setReviewError] = useState("");
  const [preview, setPreview] = useState<ShipmentPreviewResponse | null>(null);
  const [sourceReview, setSourceReview] = useState<SourceReviewResponse | null>(null);
  const [sourceReviewLoading, setSourceReviewLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState<ShipmentSubmitResponse | null>(null);
  const [appendScheduler, setAppendScheduler] = useState(true);
  const [overrideFactsWriteGate, setOverrideFactsWriteGate] = useState(false);
  const [overrideSchedulerWriteGate, setOverrideSchedulerWriteGate] = useState(false);
  const [aiReview, setAiReview] = useState<AiReviewResponse | null>(null);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [readinessWarningsAcknowledged, setReadinessWarningsAcknowledged] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [notificationTo, setNotificationTo] = useState("");
  const [notificationCc, setNotificationCc] = useState("");
  const [notificationIncludePending, setNotificationIncludePending] = useState(false);
  const [notificationPreview, setNotificationPreview] = useState<OttoNotificationPreview | null>(null);
  const [notificationSettings, setNotificationSettings] = useState<OttoNotificationSettings | null>(null);
  const [notificationStatus, setNotificationStatus] = useState("");
  const [notificationError, setNotificationError] = useState("");
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [operatorAlerts, setOperatorAlerts] = useState<OttoOperatorAlert[]>([]);
  const [operatorToasts, setOperatorToasts] = useState<OttoOperatorAlert[]>([]);
  const [lastOperatorAlertAt, setLastOperatorAlertAt] = useState("");
  const [selectedWorkspace, setSelectedWorkspace] = useState(customerWorkspaces[0]?.customerCode ?? "");
  const [activeSection, setActiveSection] = useState("dashboard");
  const [estesShipment, setEstesShipment] = useState<EstesShipmentInput>(estesDefaultShipment);
  const [estesSubject, setEstesSubject] = useState(estesSampleContext.subject);
  const [estesBody, setEstesBody] = useState(estesSampleContext.body);
  const [estesSourceLabel, setEstesSourceLabel] = useState(estesSampleContext.source);
  const [estesMode, setEstesMode] = useState<"full" | "mandatory_only">("full");
  const [estesResult, setEstesResult] = useState<EstesNormalizeResponse | null>(null);
  const [estesForwarders, setEstesForwarders] = useState<EstesForwardersResponse | null>(null);
  const [estesStatus, setEstesStatus] = useState("");
  const [estesError, setEstesError] = useState("");

  const liveMetricCards = useMemo(() => {
    if (!status) return metrics;
    return [
      { label: "Email Intake", value: String(status.counts.inboundEmails), detail: "Inbound rows in Worker D1", tone: "blue" as const },
      { label: "Needs Review", value: String(status.counts.needsReview), detail: "Held before live side effects", tone: status.counts.needsReview ? "amber" as const : "green" as const },
      { label: "FACTS XML", value: status.gates.factsWriteEnabled ? "Enabled" : "Gated", detail: status.credentialPosture.factsBaseUrlBinding ? "Secret Store binding present" : "Binding missing", tone: status.gates.factsWriteEnabled ? "amber" as const : "green" as const },
      { label: "Scheduler", value: status.gates.schedulerWriteEnabled ? "Enabled" : "Gated", detail: `${status.scheduler.tableName || "No table"} / ${status.scheduler.columnCount} columns`, tone: status.gates.schedulerWriteEnabled ? "amber" as const : "slate" as const },
      { label: "Profile Source", value: status.keyMeProfile.customerCode, detail: `${status.keyMeProfile.pallets} pallet / ${status.keyMeProfile.weight} lb`, tone: "green" as const }
    ];
  }, [status]);

  const isFallbackQueue = reviewShipments.length === 0;
  const visibleShipments = isFallbackQueue ? fallbackReviewShipments : reviewShipments;
  const selectedShipment = visibleShipments.find((shipment) => shipment.id === selectedShipmentId) ?? visibleShipments[0];
  const liveShipmentReady = !isFallbackQueue && Boolean(selectedShipment?.editable);
  const warningAcknowledgements = aiReview?.readiness.warningKeys ?? [];
  const readinessWarningsSatisfied = (aiReview?.readiness.warnCount ?? 0) === 0 || readinessWarningsAcknowledged;
  const readinessSubmitReady = liveShipmentReady && Boolean(aiReview?.readiness.submitEligible) && readinessWarningsSatisfied;
  const accessToken = session?.access_token ?? "";
  const signedInEmail = session?.user.email ?? "";
  const adminSessionReady = Boolean(accessToken) && status?.gates.adminApiEnabled === true;
  const selectedCustomerWorkspace = customerWorkspaces.find((workspace) => workspace.customerCode === selectedWorkspace) ?? customerWorkspaces[0];

  useEffect(() => {
    let cancelled = false;
    bootstrapOttoClient()
      .then(({ config, supabase: client, configError }) => {
        if (cancelled) return;
        setBootConfig(config);
        setSupabase(client);
        setBootError(configError);
      })
      .catch((error: Error) => {
        if (!cancelled) setBootError(error.message || "Unable to reach Otto worker");
      })
      .finally(() => {
        if (!cancelled) setBootLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(ottoStatusUrl, { cache: "no-store" })
      .then((response) => {
        if (!response.ok) throw new Error(`Worker status HTTP ${response.status}`);
        return response.json() as Promise<OttoPublicStatus>;
      })
      .then((payload) => {
        if (!cancelled) {
          setStatus(payload);
          setStatusError("");
        }
      })
      .catch((error: Error) => {
        if (!cancelled) setStatusError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!supabase) {
      setSessionLoaded(true);
      return;
    }
    let cancelled = false;
    setSessionLoaded(false);
    supabase.auth.getSession()
      .then(({ data }) => {
        if (!cancelled) {
          setSession(data.session);
          setSessionLoaded(true);
        }
      })
      .catch((error: Error) => {
        if (!cancelled) {
          setAuthError(error.message);
          setSessionLoaded(true);
        }
      });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession) {
        setAuthError("");
        setAuthStatus("Signed in");
      }
    });
    return () => {
      cancelled = true;
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  useEffect(() => {
    if (!selectedShipment) return;
    setReviewForm(normalizeReviewJob(selectedShipment.job));
    setPreview(null);
    setSourceReview(null);
    setSubmitResult(null);
    setReviewError("");
    setOverrideFactsWriteGate(false);
    setOverrideSchedulerWriteGate(false);
    setAiReview(null);
    setReadinessWarningsAcknowledged(false);
  }, [selectedShipment]);

  const loadReviewQueue = useCallback(async () => {
    if (!adminSessionReady) return;
    setReviewStatus("Loading review queue");
    setReviewError("");
    try {
      const payload = await callOttoAdmin<{ shipments: ReviewShipmentRow[] }>("/otto/admin/shipments/review-queue?limit=25", accessToken);
      setReviewShipments(payload.shipments ?? []);
      if (payload.shipments?.[0]) setSelectedShipmentId(payload.shipments[0].id);
      setReviewStatus(`Loaded ${payload.shipments?.length ?? 0} review rows`);
    } catch (error) {
      setReviewError(error instanceof Error ? error.message : "Unable to load review queue");
      setReviewStatus("");
    }
  }, [adminSessionReady, accessToken]);

  useEffect(() => {
    void loadReviewQueue();
  }, [loadReviewQueue]);

  const loadEstesForwarders = useCallback(async () => {
    if (!adminSessionReady) return;
    try {
      const payload = await callOttoAdmin<EstesForwardersResponse>("/otto/admin/estes/forwarders", accessToken);
      setEstesForwarders(payload);
    } catch (error) {
      setEstesError(error instanceof Error ? error.message : "Unable to load forwarder catalog");
    }
  }, [adminSessionReady, accessToken]);

  useEffect(() => {
    void loadEstesForwarders();
  }, [loadEstesForwarders]);

  const loadNotificationSettings = useCallback(async () => {
    if (!adminSessionReady) return;
    try {
      const payload = await callOttoAdmin<OttoNotificationSettings>("/otto/admin/notifications/settings", accessToken);
      setNotificationSettings(payload);
      setNotificationTo(payload.to.join("; "));
      setNotificationCc(payload.cc.join("; "));
      setNotificationIncludePending(payload.includePending);
    } catch (error) {
      setNotificationError(error instanceof Error ? error.message : "Unable to load notification settings");
    }
  }, [adminSessionReady, accessToken]);

  useEffect(() => {
    void loadNotificationSettings();
  }, [loadNotificationSettings]);

  const loadOperatorAlerts = useCallback(async (mode: "initial" | "poll" = "initial") => {
    if (!adminSessionReady) return;
    try {
      const path = lastOperatorAlertAt && mode === "poll"
        ? `/otto/admin/operator-alerts?limit=10&after=${encodeURIComponent(lastOperatorAlertAt)}`
        : "/otto/admin/operator-alerts?limit=10";
      const payload = await callOttoAdmin<{ alerts: OttoOperatorAlert[] }>(path, accessToken);
      const alerts = payload.alerts ?? [];
      if (alerts.length === 0) return;
      const newest = alerts.reduce((max, alert) => alert.createdAt > max ? alert.createdAt : max, lastOperatorAlertAt);
      setLastOperatorAlertAt(newest);
      setOperatorAlerts((current) => mergeAlerts(alerts, current).slice(0, 20));
      if (mode === "poll") {
        setOperatorToasts((current) => mergeAlerts(alerts, current).slice(0, 4));
        for (const alert of alerts.slice().reverse()) playOperatorTone(alert);
      }
    } catch {
      // Operator alerts are advisory UI; keep the workbench usable if polling fails.
    }
  }, [adminSessionReady, accessToken, lastOperatorAlertAt]);

  useEffect(() => {
    void loadOperatorAlerts("initial");
  }, [loadOperatorAlerts]);

  useEffect(() => {
    if (!adminSessionReady) return;
    const id = window.setInterval(() => {
      void loadOperatorAlerts("poll");
    }, 15000);
    return () => window.clearInterval(id);
  }, [adminSessionReady, loadOperatorAlerts]);

  useEffect(() => {
    if (operatorToasts.length === 0) return;
    const id = window.setTimeout(() => {
      setOperatorToasts((current) => current.slice(0, Math.max(0, current.length - 1)));
    }, 7000);
    return () => window.clearTimeout(id);
  }, [operatorToasts]);

  const updateEstesField = (field: keyof EstesShipmentInput, value: string) => {
    setEstesShipment((current) => ({ ...current, [field]: value }));
    setEstesResult(null);
  };

  const runEstesNormalize = async () => {
    if (!adminSessionReady) return;
    setEstesStatus("Normalizing ESTES002 shipment");
    setEstesError("");
    try {
      const payload = await callOttoAdmin<EstesNormalizeResponse>("/otto/admin/estes/normalize", accessToken, {
        method: "POST",
        body: JSON.stringify({
          shipment: estesShipment,
          context: {
            customerCode: estesShipment.customerCode || "ESTES002",
            subject: estesSubject,
            body: estesBody,
            source: estesSourceLabel
          },
          mode: estesMode
        })
      });
      setEstesResult(payload);
      setEstesShipment(payload.shipment);
      setEstesStatus(payload.changed
        ? `Applied ${payload.notes.length} rule${payload.notes.length === 1 ? "" : "s"}`
        : "No rules applied (shipment already normalized)");
    } catch (error) {
      setEstesResult(null);
      setEstesError(error instanceof Error ? error.message : "Unable to normalize shipment");
      setEstesStatus("");
    }
  };

  const applyEstesDock = async (dockKey: string, target: "consignee" | "shipper") => {
    if (!adminSessionReady) return;
    setEstesStatus(`Applying ${dockKey} to ${target}`);
    setEstesError("");
    try {
      const payload = await callOttoAdmin<{ ok: boolean; carrier: string; shipment: EstesShipmentInput }>(
        "/otto/admin/estes/apply-dock",
        accessToken,
        {
          method: "POST",
          body: JSON.stringify({ shipment: estesShipment, dockKey, target })
        }
      );
      if (payload.shipment) {
        setEstesShipment(payload.shipment);
        setEstesStatus(`Applied ${payload.carrier} PHX dock to ${target}`);
      }
    } catch (error) {
      setEstesError(error instanceof Error ? error.message : "Unable to apply forwarder dock");
      setEstesStatus("");
    }
  };

  const resetEstesShipment = () => {
    setEstesShipment(estesDefaultShipment);
    setEstesSubject(estesSampleContext.subject);
    setEstesBody(estesSampleContext.body);
    setEstesSourceLabel(estesSampleContext.source);
    setEstesResult(null);
    setEstesStatus("Reset to ESTES002 sample shipment");
    setEstesError("");
  };

  const sendMagicLink = async () => {
    if (!supabase || !authEmail.trim()) return;
    setAuthLoading(true);
    setAuthError("");
    setAuthStatus("");
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: authEmail.trim(),
        options: {
          emailRedirectTo: window.location.origin
        }
      });
      if (error) throw error;
      setAuthStatus("Magic link sent — check your inbox");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Unable to send magic link");
    } finally {
      setAuthLoading(false);
    }
  };

  const signInWithPassword = async () => {
    if (!supabase || !authEmail.trim() || !authPassword) return;
    setAuthLoading(true);
    setAuthError("");
    setAuthStatus("");
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: authEmail.trim(),
        password: authPassword
      });
      if (error) throw error;
      setAuthPassword("");
      setAuthStatus("Signed in");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Unable to sign in");
    } finally {
      setAuthLoading(false);
    }
  };

  const sendPasswordReset = async () => {
    if (!supabase || !authEmail.trim()) return;
    setAuthLoading(true);
    setAuthError("");
    setAuthStatus("");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(authEmail.trim(), {
        redirectTo: window.location.origin
      });
      if (error) throw error;
      setAuthStatus("Password reset email sent");
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : "Unable to send password reset");
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = async () => {
    if (supabase) await supabase.auth.signOut();
    setSession(null);
    setReviewShipments([]);
    setReviewStatus("");
    setReviewError("");
    setAuthStatus("Signed out");
    setAuthPassword("");
  };

  const updateReviewField = (field: keyof ReviewShipmentJob, value: string) => {
    setReviewForm((current) => ({
      ...current,
      [field]: value,
      customerCode: "HASSE001"
    }));
    setPreview(null);
    setSubmitResult(null);
    setAiReview(null);
    setReadinessWarningsAcknowledged(false);
  };

  const setSchedulingConfirmed = (confirmed: boolean) => {
    setReviewForm((current) => ({
      ...current,
      scheduledDateConfirmed: confirmed,
      customerCode: "HASSE001"
    }));
    setPreview(null);
    setSubmitResult(null);
    setAiReview(null);
    setReadinessWarningsAcknowledged(false);
  };

  const seedTestRow = async () => {
    if (!adminSessionReady) return;
    setSeedLoading(true);
    setReviewError("");
    setReviewStatus("Seeding test row in D1 inbound ledger");
    try {
      const payload = await callOttoAdmin<SeedTestShipmentResponse>("/otto/admin/shipments/seed-test", accessToken, {
        method: "POST",
        body: JSON.stringify({ job: reviewForm })
      });
      setReviewStatus(payload.detail);
      await loadReviewQueue();
    } catch (error) {
      setReviewError(error instanceof Error ? error.message : "Unable to seed test row");
      setReviewStatus("");
    } finally {
      setSeedLoading(false);
    }
  };

  const runAiReview = async () => {
    if (!adminSessionReady) return;
    setAiReviewLoading(true);
    setReviewError("");
    try {
      const payload = await callOttoAdmin<AiReviewResponse>("/otto/admin/shipments/ai-review", accessToken, {
        method: "POST",
        body: JSON.stringify({ job: reviewForm })
      });
      setAiReview(payload);
      setReadinessWarningsAcknowledged(false);
    } catch (error) {
      setAiReview(null);
      setReadinessWarningsAcknowledged(false);
      setReviewError(error instanceof Error ? error.message : "AI review failed");
    } finally {
      setAiReviewLoading(false);
    }
  };

  const previewShipment = async () => {
    if (!adminSessionReady) return;
    setReviewStatus("Previewing FACTS XML");
    setReviewError("");
    setSubmitResult(null);
    try {
      const payload = await callOttoAdmin<ShipmentPreviewResponse>("/otto/admin/shipments/preview", accessToken, {
        method: "POST",
        body: JSON.stringify({ job: reviewForm })
      });
      setPreview(payload);
      setReviewStatus(payload.ok ? "Preview ready" : "Preview has validation errors");
    } catch (error) {
      setPreview(null);
      setReviewError(error instanceof Error ? error.message : "Unable to preview shipment");
      setReviewStatus("");
    }
  };

  const loadSourceReview = async () => {
    if (!adminSessionReady || isFallbackQueue || !selectedShipment) return;
    setSourceReviewLoading(true);
    setReviewError("");
    setReviewStatus("Loading source review");
    try {
      const payload = await callOttoAdmin<SourceReviewResponse>(
        `/otto/admin/shipments/source-review?dedupeKey=${encodeURIComponent(selectedShipment.dedupeKey)}`,
        accessToken
      );
      setSourceReview(payload);
      setReviewStatus(payload.ok ? "Source review ready" : "Source review has blockers");
    } catch (error) {
      setSourceReview(null);
      setReviewError(error instanceof Error ? error.message : "Unable to load source review");
      setReviewStatus("");
    } finally {
      setSourceReviewLoading(false);
    }
  };

  const submitShipment = async () => {
    if (!adminSessionReady) return;
    setReviewStatus("Submitting reviewed shipment");
    setReviewError("");
    try {
      const payload = await callOttoAdmin<ShipmentSubmitResponse>("/otto/admin/shipments/submit", accessToken, {
        method: "POST",
        body: JSON.stringify({
          job: reviewForm,
          confirm: reviewSubmitConfirm,
          appendScheduler,
          acknowledgedWarnings: readinessWarningsSatisfied ? warningAcknowledgements : [],
          overrideFactsWriteGate,
          overrideSchedulerWriteGate: appendScheduler && overrideSchedulerWriteGate
        })
      });
      setSubmitResult(payload);
      setReviewStatus(payload.blocked ? "Submit blocked by Worker gate" : payload.ok ? "Submit completed" : "Submit returned errors");
    } catch (error) {
      setSubmitResult(null);
      setReviewError(error instanceof Error ? error.message : "Unable to submit shipment");
      setReviewStatus("");
    }
  };

  // One-click readiness: run the source review + FACTS dry-run preview + AI
  // review back-to-back so the operator makes one decision instead of clicking
  // three buttons. Each call owns its own error handling; Submit stays gated on
  // the resulting `readinessSubmitReady`.
  const prepareForSubmit = async () => {
    if (!adminSessionReady || !liveShipmentReady) return;
    setPreparing(true);
    setReviewError("");
    try {
      if (!isFallbackQueue) await loadSourceReview();
      await previewShipment();
      await runAiReview();
    } finally {
      setPreparing(false);
    }
  };

  const previewNotificationEmail = async () => {
    if (!adminSessionReady) return;
    setNotificationLoading(true);
    setNotificationError("");
    setNotificationStatus("Building Resend digest preview");
    try {
      const payload = await callOttoAdmin<OttoNotificationPreview>("/otto/admin/notifications/preview", accessToken, {
        method: "POST",
        body: JSON.stringify({
          to: notificationTo,
          cc: notificationCc,
          includePending: notificationIncludePending
        })
      });
      setNotificationPreview(payload);
      setNotificationStatus(`Digest preview ready: ${payload.rows.length} successful submission${payload.rows.length === 1 ? "" : "s"}`);
    } catch (error) {
      setNotificationPreview(null);
      setNotificationError(error instanceof Error ? error.message : "Notification preview failed");
      setNotificationStatus("");
    } finally {
      setNotificationLoading(false);
    }
  };

  const saveNotificationSettings = async () => {
    if (!adminSessionReady) return;
    setNotificationLoading(true);
    setNotificationError("");
    setNotificationStatus("Saving notification recipients");
    try {
      const payload = await callOttoAdmin<OttoNotificationSettings>("/otto/admin/notifications/settings", accessToken, {
        method: "POST",
        body: JSON.stringify({
          to: notificationTo,
          cc: notificationCc,
          includePending: notificationIncludePending,
          digestWindowMinutes: notificationSettings?.digestWindowMinutes ?? status?.notifications.digestWindowMinutes ?? 15
        })
      });
      setNotificationSettings(payload);
      setNotificationTo(payload.to.join("; "));
      setNotificationCc(payload.cc.join("; "));
      setNotificationIncludePending(payload.includePending);
      setNotificationStatus("Notification recipients saved");
    } catch (error) {
      setNotificationError(error instanceof Error ? error.message : "Unable to save notification settings");
      setNotificationStatus("");
    } finally {
      setNotificationLoading(false);
    }
  };

  const sendNotificationEmail = async () => {
    if (!adminSessionReady) return;
    setNotificationLoading(true);
    setNotificationError("");
    setNotificationStatus("Sending Otto notification email through Resend");
    try {
      const payload = await callOttoAdmin<OttoNotificationPreview>("/otto/admin/notifications/send", accessToken, {
        method: "POST",
        body: JSON.stringify({
          to: notificationTo,
          cc: notificationCc,
          includePending: notificationIncludePending,
          windowStart: notificationPreview?.windowStart,
          windowEnd: notificationPreview?.windowEnd,
          confirm: notificationSendConfirm
        })
      });
      setNotificationPreview(payload);
      setNotificationStatus(payload.sent ? "Resend notification email sent" : "Notification request completed");
    } catch (error) {
      setNotificationError(error instanceof Error ? error.message : "Notification send failed");
      setNotificationStatus("");
    } finally {
      setNotificationLoading(false);
    }
  };

  const adminActionLabel = status?.gates.adminApiEnabled
    ? session
      ? "Supabase session"
      : "sign in required"
    : "admin API off";

  const navSections = [
    { id: "dashboard", label: "Dashboard", icon: "dashboard" },
    { id: "keyme-review", label: "Shipment Review", icon: "review" },
    { id: "customers", label: "Customers", icon: "customers" },
    { id: "estes-workbench", label: "ESTES002", icon: "estes" },
    { id: "automation-path", label: "Automation Path", icon: "route" },
    { id: "events", label: "Events", icon: "events" },
    { id: "evidence", label: "Evidence", icon: "evidence" },
    { id: "setup", label: "Setup", icon: "setup" }
  ] as const;

  const goToSection = (id: string) => {
    setActiveSection(id);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ----- Hard auth gate ----------------------------------------------------
  // Until the worker config has loaded AND Supabase has restored the session,
  // render a minimal shell. No workbench data is fetched from the admin API
  // before this point.
  if (bootLoading) {
    return <BootSplash subtitle="Bootstrapping Otto config…" />;
  }
  if (bootError && !supabase) {
    return (
      <BootGate
        title="Otto setup required"
        subtitle="The Supabase publishable key is not bound on the Otto worker."
        body={
          <>
            <p>
              The portal fetches its Supabase config from{" "}
              <code>{bootConfig?.workerOrigin ?? "the Otto worker"}/otto/public/config</code>.
              That request succeeded, but the worker hasn't been given a publishable key yet.
            </p>
            <p>Run this once on the operator workstation, then redeploy the worker:</p>
            <pre className="bootCode">{`# 1. Copy the anon (publishable) key from Supabase
#    dashboard -> Project Settings -> API.
# 2. Push it into Cloudflare Secret Store:
wrangler secrets-store secret create \\
  --store-id a8a96e35a308490ca37de879b2195232 \\
  --name SUPABASE_PUBLISHABLE_KEY \\
  --value 'PASTE_KEY_HERE'
# 3. Redeploy the worker so it picks up the new binding:
cd hassett-keyme-ingest && npx wrangler deploy`}</pre>
            <p className="bootError">Worker reported: {bootError}</p>
          </>
        }
      />
    );
  }
  if (!supabase) {
    return (
      <BootGate
        title="Otto worker unreachable"
        subtitle="The portal could not contact the Otto worker to bootstrap Supabase config."
        body={
          <>
            <p>{bootError || "Unknown bootstrap error."}</p>
            <button type="button" className="primaryButton" onClick={() => window.location.reload()}>
              Retry
            </button>
          </>
        }
      />
    );
  }
  if (!sessionLoaded) {
    return <BootSplash subtitle="Restoring session…" />;
  }
  if (!session) {
    return (
      <SignInScreen
        bootConfig={bootConfig}
        authEmail={authEmail}
        setAuthEmail={setAuthEmail}
        authPassword={authPassword}
        setAuthPassword={setAuthPassword}
        authMode={authMode}
        setAuthMode={(mode) => {
          setAuthMode(mode);
          setAuthError("");
          setAuthStatus("");
        }}
        authLoading={authLoading}
        authStatus={authStatus}
        authError={authError}
        onSignInWithPassword={() => void signInWithPassword()}
        onSendMagicLink={() => void sendMagicLink()}
        onResetPassword={() => void sendPasswordReset()}
      />
    );
  }

  return (
    <div className="appShell">
      <OperatorToastStack alerts={operatorToasts} onDismiss={(id) => setOperatorToasts((current) => current.filter((alert) => alert.id !== id))} />
      <aside className="sidebar" aria-label="Otto navigation">
        <div className="brandBlock">
          <div className="brandMark" aria-hidden="true">O</div>
          <div>
            <div className="brandName">Otto</div>
            <div className="brandUrl">otto.ldtws.dev</div>
          </div>
        </div>
        <nav className="navStack">
          {navSections.map((item) => (
            <button
              type="button"
              className={activeSection === item.id ? "navItem active" : "navItem"}
              key={item.id}
              onClick={() => goToSection(item.id)}
            >
              <NavIcon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebarFooter">
          <div className="smallLabel">Session</div>
          <div className={`envPill ${session ? "envPill--on" : ""}`}>
            <span className={`dot ${session ? "green" : "amber"}`} />
            {session ? (signedInEmail || "Signed in") : "Signed out"}
          </div>
          <div className="smallLabel">Environment</div>
          <div className="envPill"><span className="dot green" /> Dry-run only</div>
        </div>
      </aside>

      <main className="workspace" id="dashboard">
        <header className="topbar">
          <div className="topbarHeading">
            <div className="smallLabel">Operations</div>
            <h1>Otto Command Center</h1>
            <p>Hassett / KeyMe lane, ESTES002 routing, and Outlook LDT bridge — all dry-run-first.</p>
          </div>
          <div className="topActions">
            <StatusBadge tone={status ? "green" : statusError ? "red" : "amber"}>
              {status ? "Worker connected" : statusError ? "Worker unreachable" : "Checking Worker…"}
            </StatusBadge>
            <StatusBadge tone={status?.gates.factsWriteEnabled ? "amber" : "green"}>
              FACTS {status?.gates.factsWriteEnabled ? "write on" : "write gated"}
            </StatusBadge>
            <StatusBadge tone={status?.gates.wideNetTrainingEnabled ? "blue" : "slate"}>
              Wide-net {status?.gates.wideNetTrainingEnabled ? "training" : "off"}
            </StatusBadge>
            <div className="userChip">
              <span className="userEmail">{signedInEmail}</span>
              <button type="button" className="userSignOut" onClick={() => void signOut()}>Sign out</button>
            </div>
          </div>
        </header>


        {activeSection === "dashboard" && (
        <>
        <section className="metricGrid" aria-label="Current Otto status">
          {liveMetricCards.map((metric) => (
            <article className={`metricCard ${metric.tone}`} key={metric.label}>
              <div className="metricTop">
                <span>{metric.label}</span>
                <StatusDot tone={metric.tone} />
              </div>
              <strong>{metric.value}</strong>
              <p>{metric.detail}</p>
            </article>
          ))}
        </section>

        <section className="statusBand" aria-label="Live Worker status">
          <div className="statusSummary">
            <PanelHeader title="Live Worker Status" action={status ? formatTime(status.generatedAt) : "loading"} />
            <div className="summaryGrid">
              <SummaryItem label="HTTP ingest" value={status ? onOff(status.gates.httpIngestEnabled) : "Checking"} tone={status?.gates.httpIngestEnabled ? "amber" : "slate"} />
              <SummaryItem label="FACTS writes" value={status ? onOff(status.gates.factsWriteEnabled) : "Checking"} tone={status?.gates.factsWriteEnabled ? "amber" : "green"} />
              <SummaryItem label="Scheduler writes" value={status ? onOff(status.gates.schedulerWriteEnabled) : "Checking"} tone={status?.gates.schedulerWriteEnabled ? "amber" : "green"} />
              <SummaryItem label="FACTS binding" value={status?.credentialPosture.factsBaseUrlBinding ? "present" : "not confirmed"} tone={status?.credentialPosture.factsBaseUrlBinding ? "green" : "amber"} />
              <SummaryItem label="Graph scheduler" value={status?.credentialPosture.graphAuthConfigured ? "configured" : "not configured"} tone={status?.credentialPosture.graphAuthConfigured ? "green" : "slate"} />
              <SummaryItem label="Admin auth" value={status?.adminAuth.mode ?? "checking"} tone={status?.credentialPosture.supabaseAuthConfigured ? "green" : "amber"} />
              <SummaryItem label="Wide-net" value={status?.gates.wideNetTrainingEnabled ? "training" : "off"} tone={status?.gates.wideNetTrainingEnabled ? "blue" : "slate"} />
              <SummaryItem label="Wide-net FACTS" value={status?.gates.wideNetFactsWriteEnabled ? "specific on" : "off"} tone={status?.gates.wideNetFactsWriteEnabled ? "amber" : "green"} />
              <SummaryItem label="Auto-submit" value={status?.gates.wideNetAutoSubmitEnabled && status?.gates.wideNetFactsWriteEnabled ? "armed" : "gated"} tone={status?.gates.wideNetAutoSubmitEnabled && status?.gates.wideNetFactsWriteEnabled ? "amber" : "green"} />
            </div>
            {statusError && <div className="inlineAlert">Worker status unavailable: {statusError}</div>}
          </div>
        </section>
        </>
        )}

        {operatorAlerts.length > 0 && (
          <section className="operatorAlertBand" aria-label="Operator alerts">
            <PanelHeader title="Operator Alerts" action={`${operatorAlerts.length} recent`} />
            <div className="operatorAlertList">
              {operatorAlerts.slice(0, 5).map((alert) => (
                <article className={`operatorAlert operatorAlert--${alert.severity}`} key={alert.id}>
                  <div>
                    <strong>{alert.title}</strong>
                    <p>{alert.body}</p>
                  </div>
                  <span>{formatTime(alert.createdAt)}</span>
                </article>
              ))}
            </div>
          </section>
        )}

        {activeSection === "keyme-review" && (
        <section className="tableBand workbenchBand" id="keyme-review" aria-label="Shipment review workbench">
          <div className="tableToolbar">
            <PanelHeader title="Shipment Review Workbench" action={adminActionLabel} />
            <div className="adminControls">
              <button type="button" onClick={() => void loadReviewQueue()} disabled={!adminSessionReady}>Refresh</button>
            </div>
          </div>

          <DecisionQueue
            shipments={visibleShipments}
            selectedId={selectedShipment?.id ?? selectedShipmentId}
            onPick={(id) => {
              setSelectedShipmentId(id);
              if (typeof window !== "undefined") {
                window.requestAnimationFrame(() => {
                  document.querySelector(".reviewEditor")?.scrollIntoView({ behavior: "smooth", block: "start" });
                });
              }
            }}
            loading={reviewStatus.startsWith("Loading")}
            error={reviewError}
            isSample={isFallbackQueue}
          />

          <div className="workbenchGrid">
            <div className="reviewEditor">
              <div className="editorHeader">
                <div>
                  <div className="smallLabel">{editorFamilyLabel(reviewForm)}</div>
                  <h2>{reviewForm.task || "Shipment action"}</h2>
                </div>
                <div className="gateInline">
                  <StatusBadge tone={selectedShipment?.editable ? "green" : "amber"}>{selectedShipment?.editable ? "editable" : "held"}</StatusBadge>
                  <StatusBadge tone="green">{reviewForm.customerCode}</StatusBadge>
                </div>
              </div>

              {sourceReview?.sourceEvidence?.emailBodyPreview && (
                <details className="sourceEmailInline" open>
                  <summary>
                    Source email{sourceReview.row.subject ? ` — ${sourceReview.row.subject}` : ""}
                  </summary>
                  <div className="sourceEmailMeta">From {sourceReview.row.from || "unknown sender"}</div>
                  <pre className="sourceEmailBody">{sourceReview.sourceEvidence.emailBodyPreview}</pre>
                  {sourceReview.sourceEvidence.attachmentNote && (
                    <div className="sourceEmailMeta">{sourceReview.sourceEvidence.attachmentNote}</div>
                  )}
                </details>
              )}

              {(() => {
                // Surface-by-exception: blank required fields ride on top; the
                // rest collapse behind a disclosure ("Fix something" = open it).
                // Exception keys come from the parser's missingRequired.
                const missingSet = new Set((reviewForm.missingRequired ?? []).map(String));
                const labelFor = (key: string): string =>
                  (({
                    latestDate: "Latest date",
                    scheduleUrl: "Schedule link",
                    poNumber: "PO #",
                    keyMeId: "KeyMe ID",
                    task: "Task",
                    siteName: "Site name",
                    address1: "Address",
                    city: "City",
                    state: "State",
                    zip: "ZIP"
                  }) as Record<string, string>)[key] ?? key;
                const renderField = (field: (typeof editableFields)[number]) => {
                  const isException = missingSet.has(String(field.key));
                  return (
                    <label
                      className={`field${field.span === "wide" ? " wide" : ""}${isException ? " fieldNeedsYou" : ""}`}
                      key={field.key}
                    >
                      <span>{field.label}</span>
                      {field.key === "jobNotes" ? (
                        <textarea
                          rows={4}
                          value={String(reviewForm[field.key] ?? "")}
                          onChange={(event) => updateReviewField(field.key, event.target.value)}
                        />
                      ) : (
                        <input
                          value={String(reviewForm[field.key] ?? "")}
                          onChange={(event) => updateReviewField(field.key, event.target.value)}
                        />
                      )}
                    </label>
                  );
                };
                const exceptionFields = editableFields.filter((f) => missingSet.has(String(f.key)));
                const settledFields = editableFields.filter((f) => !missingSet.has(String(f.key)));
                const missingNotEditable = (reviewForm.missingRequired ?? []).filter(
                  (key) => !editableFields.some((f) => String(f.key) === key)
                );
                return (
                  <div className="editorFields">
                    {exceptionFields.length > 0 ? (
                      <div className="editorExceptions">
                        <div className="editorGroupLabel needsYou">
                          ⚠ Otto needs you on {exceptionFields.length} field{exceptionFields.length === 1 ? "" : "s"}
                        </div>
                        <div className="formGrid">{exceptionFields.map(renderField)}</div>
                      </div>
                    ) : (
                      <div className="editorConfident">
                        ✓ No required field needs you — review and submit, or open all fields below.
                      </div>
                    )}
                    {missingNotEditable.length > 0 && (
                      <div className="editorMissingNote">
                        Also required but not editable here: {missingNotEditable.map(labelFor).join(", ")}. Fix upstream / re-ingest.
                      </div>
                    )}
                    <details className="editorAllFields">
                      <summary>
                        {exceptionFields.length > 0
                          ? `Other fields (${settledFields.length})`
                          : `All fields (${settledFields.length})`}
                      </summary>
                      <div className="formGrid">{settledFields.map(renderField)}</div>
                    </details>
                  </div>
                );
              })()}

              <SchedulingBanner
                confirmed={reviewForm.scheduledDateConfirmed}
                onToggle={setSchedulingConfirmed}
                weekEndingDate={reviewForm.weekEndingDate}
                suggestedServiceDate={reviewForm.suggestedServiceDate}
                requestedCompletionDate={String((reviewForm as unknown as Record<string, unknown>).requestedCompletionDate ?? "")}
                schedulingContact={pickSchedulingContactDisplay(reviewForm)}
              />

              {isFallbackQueue && (
                <div className="fallbackQueueBanner">
                  <div>
                    <strong>Demo queue — no live D1 row</strong>
                    <p>
                      This entry is portal-side fallback data, so Preview / AI Review / Submit are disabled
                      to avoid foreign-key errors against <code>inbound_emails</code>. Click "Seed Test Row"
                      to insert this job into the D1 inbound ledger and unlock the workflow.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="primaryButton"
                    onClick={() => void seedTestRow()}
                    disabled={!adminSessionReady || seedLoading}
                  >
                    {seedLoading ? "Seeding…" : "Seed Test Row"}
                  </button>
                </div>
              )}

              <div className="submitStrip">
                <div className="submitToggles">
                  <label className="checkboxRow">
                    <input checked={appendScheduler} onChange={(event) => setAppendScheduler(event.target.checked)} type="checkbox" />
                    <span>Append scheduler row after accepted FACTS PRO</span>
                  </label>
                  {adminSessionReady && status?.gates.factsWriteEnabled === false && (
                    <label className="checkboxRow checkboxRow--override">
                      <input
                        checked={overrideFactsWriteGate}
                        onChange={(event) => setOverrideFactsWriteGate(event.target.checked)}
                        type="checkbox"
                      />
                      <span>
                        Override <code>FACTS_WRITE_ENABLED</code> for this entry
                        <small>Global gate is off. Logs a <code>facts_write_gate_overridden</code> event under {signedInEmail || "this session"}.</small>
                      </span>
                    </label>
                  )}
                  {adminSessionReady && appendScheduler && status?.gates.schedulerWriteEnabled === false && (
                    <label className="checkboxRow checkboxRow--override">
                      <input
                        checked={overrideSchedulerWriteGate}
                        onChange={(event) => setOverrideSchedulerWriteGate(event.target.checked)}
                        type="checkbox"
                      />
                      <span>
                        Override <code>SCHEDULER_WRITE_ENABLED</code> for this entry
                        <small>Global gate is off. Scheduler append still requires Graph + workbook bindings.</small>
                      </span>
                    </label>
                  )}
                </div>
                <div className="submitButtons">
                  <button type="button" className="primaryButton" onClick={() => void prepareForSubmit()} disabled={!adminSessionReady || !liveShipmentReady || preparing}>
                    {preparing ? "Checking…" : "Prepare & check"}
                  </button>
                  <button type="button" className="secondaryButton" onClick={() => void loadSourceReview()} disabled={!adminSessionReady || isFallbackQueue || sourceReviewLoading}>
                    {sourceReviewLoading ? "Loading..." : "Source Review"}
                  </button>
                  <button type="button" className="secondaryButton" onClick={() => void previewShipment()} disabled={!adminSessionReady || !liveShipmentReady}>
                    Preview XML
                  </button>
                  <button type="button" className="secondaryButton" onClick={() => void runAiReview()} disabled={!adminSessionReady || aiReviewLoading || !liveShipmentReady}>
                    {aiReviewLoading ? "Reviewing…" : "AI Review"}
                  </button>
                  <button type="button" className={readinessSubmitReady ? "dangerButton readySubmit" : "dangerButton"} onClick={() => void submitShipment()} disabled={!adminSessionReady || !readinessSubmitReady}>
                    {readinessSubmitReady ? "Submit FACTS ✓" : "Submit FACTS"}
                  </button>
                </div>
              </div>

              <ReadinessContractPanel
                sourceReview={sourceReview}
                preview={preview}
                aiReview={aiReview}
                warningsAcknowledged={readinessWarningsAcknowledged}
                onAcknowledgeWarnings={setReadinessWarningsAcknowledged}
              />

              <div className="gateMatrix">
                <SummaryItem label="Admin API" value={status?.gates.adminApiEnabled ? "on" : "off"} tone={status?.gates.adminApiEnabled ? "amber" : "slate"} />
                <SummaryItem label="Duplicate check" value={status?.gates.factsDuplicateCheckEnabled ? "on" : "off"} tone={status?.gates.factsDuplicateCheckEnabled ? "green" : "red"} />
                <SummaryItem
                  label="FACTS write"
                  value={status?.gates.factsWriteEnabled ? "on" : overrideFactsWriteGate ? "off · override" : "off"}
                  tone={status?.gates.factsWriteEnabled ? "amber" : overrideFactsWriteGate ? "red" : "green"}
                />
                <SummaryItem
                  label="Scheduler write"
                  value={status?.gates.schedulerWriteEnabled ? "on" : overrideSchedulerWriteGate && appendScheduler ? "off · override" : "off"}
                  tone={status?.gates.schedulerWriteEnabled ? "amber" : overrideSchedulerWriteGate && appendScheduler ? "red" : "green"}
                />
              </div>

              {reviewStatus && <div className="inlineNote">{reviewStatus}</div>}
              {reviewError && <div className="inlineAlert">{reviewError}</div>}
              {submitResult?.blocked && <div className="inlineAlert">{submitResult.blockers.join(" | ")}</div>}
              {submitResult?.overrides?.factsWriteGate && (
                <div className="inlineAlert">
                  FACTS write gate was overridden for this entry — recorded under {signedInEmail || "this session"} in the audit log.
                </div>
              )}
              {submitResult?.overrides?.schedulerWriteGate && (
                <div className="inlineAlert">
                  Scheduler write gate was overridden for this entry — recorded under {signedInEmail || "this session"} in the audit log.
                </div>
              )}
              {submitResult?.facts && (
                <div className={submitResult.facts.success ? "inlineNote" : "inlineAlert"}>
                  FACTS {submitResult.facts.statusText}; PRO {submitResult.facts.trackingNumber || "not returned"}.
                </div>
              )}
              {submitResult?.facts?.errors?.length ? (
                <div className="inlineAlert">
                  <strong>FACTS errors:</strong>
                  <ul className="factsErrorList">
                    {submitResult.facts.errors.map((error, index) => (
                      <li key={`fact-err-${index}`}>{error}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {submitResult?.facts?.warnings?.length ? (
                <div className="inlineNote">
                  <strong>FACTS warnings:</strong>
                  <ul className="factsErrorList">
                    {submitResult.facts.warnings.map((warning, index) => (
                      <li key={`fact-warn-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {submitResult?.facts?.responseXml && (
                <details className="factsResponseDetails" open={!submitResult.facts.success}>
                  <summary>Raw FACTS server response — {submitResult.facts.responseXml.length} chars</summary>
                  <pre>{submitResult.facts.responseXml}</pre>
                </details>
              )}
              {submitResult?.facts?.requestXmlRedacted && (
                <details className="factsResponseDetails">
                  <summary>BOL XML body that was POSTed (credentials redacted)</summary>
                  <pre>{prettyXml(submitResult.facts.requestXmlRedacted)}</pre>
                </details>
              )}

              {aiReview && <AiReviewPanel review={aiReview} />}
            </div>

            <aside className="previewPane" aria-label="Preview output">
              <PanelHeader title="Preview — what Submit will write" action={preview ? (preview.ok ? "valid" : `${preview.validationErrors.length} validation issue${preview.validationErrors.length === 1 ? "" : "s"}`) : "click Preview XML"} />

              {preview && preview.validationErrors.length > 0 && (
                <div className="previewIssues">
                  <strong>Local dry-run validation errors:</strong>
                  <ul>
                    {preview.validationErrors.map((error, index) => (
                      <li key={`preview-err-${index}`}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
              {preview && preview.facts.warnings.length > 0 && (
                <div className="previewWarnings">
                  <strong>Dry-run warnings:</strong>
                  <ul>
                    {preview.facts.warnings.map((warning, index) => (
                      <li key={`preview-warn-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <details className="previewBlock" open={Boolean(preview)}>
                <summary className="smallLabel">FACTS BOL XML body that will POST to <code>tbolentry4.xml</code> (credentials redacted)</summary>
                <pre>{preview ? prettyXml(preview.facts.requestXmlRedacted) : "Click Preview XML to dry-run validation. Credentials are redacted before display."}</pre>
              </details>

              <details className="previewBlock" open={Boolean(preview)}>
                <summary className="smallLabel">
                  Scheduler row that will append to <code>{status?.scheduler.tableName || "the scheduler workbook"}</code>
                  {!appendScheduler && <span className="previewMutedHint"> — currently disabled by "Append scheduler row" checkbox</span>}
                </summary>
                <table className="schedulerPreviewTable">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview ? (
                      preview.schedulerPreview.columns.map((column, index) => (
                        <tr key={`col-${column}-${index}`}>
                          <td><code>{column}</code></td>
                          <td className="schedulerPreviewValue">{preview.schedulerPreview.byColumn[column] || <em className="schedulerPreviewBlank">(blank)</em>}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={2}><em>Preview not generated yet.</em></td></tr>
                    )}
                  </tbody>
                </table>
              </details>

              {sourceReview && <SourceReviewPanel review={sourceReview} />}
            </aside>
          </div>

          <div className="notificationPanel">
            <PanelHeader title="Entered Shipment Email" action={notificationPreview?.sent ? "sent via Resend" : notificationPreview ? "digest preview" : `${status?.notifications.digestWindowMinutes ?? 15} min digest`} />
            <div className="notificationMeta notificationMeta--settings">
              <span>From: {notificationSettings?.from ?? status?.notifications.from ?? "Otto <Otto@ldtws.dev>"}</span>
              <span>Saved: {notificationSettings?.source === "d1" ? "portal settings" : "worker defaults"}</span>
              <StatusBadge tone={(notificationSettings?.sendConfigured ?? status?.notifications.sendEnabled) ? "green" : "amber"}>
                {(notificationSettings?.sendConfigured ?? status?.notifications.sendEnabled) ? "send enabled" : "send gated"}
              </StatusBadge>
              <StatusBadge tone={(notificationSettings?.resendConfigured ?? status?.notifications.resendConfigured) ? "green" : "amber"}>
                {(notificationSettings?.resendConfigured ?? status?.notifications.resendConfigured) ? "Resend key" : "no Resend key"}
              </StatusBadge>
            </div>
            <div className="notificationControls">
              <label className="field">
                <span>To</span>
                <input value={notificationTo} onChange={(event) => setNotificationTo(event.target.value)} placeholder="dispatcher@example.com; ops@example.com" />
              </label>
              <label className="field">
                <span>CC</span>
                <input value={notificationCc} onChange={(event) => setNotificationCc(event.target.value)} placeholder="optional" />
              </label>
              <label className="checkboxRow">
                <input checked={notificationIncludePending} onChange={(event) => setNotificationIncludePending(event.target.checked)} type="checkbox" />
                <span>Include pending review rows in manual preview</span>
              </label>
              <div className="submitButtons">
                <button type="button" className="secondaryButton" onClick={() => void saveNotificationSettings()} disabled={!adminSessionReady || notificationLoading || !notificationTo.trim()}>
                  Save Recipients
                </button>
                <button type="button" className="secondaryButton" onClick={() => void previewNotificationEmail()} disabled={!adminSessionReady || notificationLoading}>
                  Preview Digest
                </button>
                <button type="button" className="primaryButton" onClick={() => void sendNotificationEmail()} disabled={!adminSessionReady || notificationLoading || !notificationPreview || notificationPreview.rows.length === 0}>
                  Send Now
                </button>
              </div>
            </div>
            {notificationStatus && <div className="inlineNote">{notificationStatus}</div>}
            {notificationError && <div className="inlineAlert">{notificationError}</div>}
            {notificationPreview && <NotificationPreviewTable preview={notificationPreview} />}
          </div>
        </section>
        )}

        {activeSection === "customers" && (
        <section className="tableBand knowledgeBand" id="customers" aria-label="Customer workspaces">
          <div className="tableToolbar">
            <PanelHeader title="Customer Workspaces" action={selectedCustomerWorkspace?.workspaceState ?? "planned"} />
            <div className="segmented" aria-label="Select customer workspace">
              {customerWorkspaces.map((workspace) => (
                <button
                  type="button"
                  className={selectedWorkspace === workspace.customerCode ? "selected" : ""}
                  key={workspace.customerCode}
                  onClick={() => setSelectedWorkspace(workspace.customerCode)}
                >
                  {workspace.customerCode}
                </button>
              ))}
            </div>
          </div>
          {selectedCustomerWorkspace && <CustomerWorkspacePanel workspace={selectedCustomerWorkspace} />}
        </section>
        )}

        {activeSection === "estes-workbench" && (
        <section className="tableBand workbenchBand" id="estes-workbench" aria-label="ESTES002 normalization workbench">
          <div className="tableToolbar">
            <PanelHeader title="ESTES002 Normalization Workbench" action={estesForwarders ? `${estesForwarders.forwarders.length} PHX docks loaded` : adminSessionReady ? "loading…" : "sign in to load docks"} />
            <div className="adminControls">
              <label className="inlineSelect">
                <span>Mode</span>
                <select
                  aria-label="Normalization mode"
                  value={estesMode}
                  onChange={(event) => setEstesMode(event.target.value === "mandatory_only" ? "mandatory_only" : "full")}
                >
                  <option value="full">Full normalization</option>
                  <option value="mandatory_only">Mandatory only</option>
                </select>
              </label>
              <button type="button" onClick={() => resetEstesShipment()}>Reset</button>
              <button type="button" className="primaryButton" onClick={() => void runEstesNormalize()} disabled={!adminSessionReady}>
                Normalize
              </button>
            </div>
          </div>

          <div className="workbenchGrid">
            <div className="reviewEditor">
              <div className="editorHeader">
                <div>
                  <div className="smallLabel">ESTES002 shipment</div>
                  <h2>{estesShipment.shipName || "Shipper pending"} → {estesShipment.consName || "Consignee pending"}</h2>
                </div>
                <div className="gateInline">
                  <StatusBadge tone={estesResult?.inServiceArea ? "green" : "amber"}>
                    {estesResult?.inServiceArea ? "in AZ/CA service area" : "out of service area"}
                  </StatusBadge>
                  <StatusBadge tone="green">{estesShipment.billToAccount || "ESTES002"}</StatusBadge>
                </div>
              </div>

              <div className="fieldGroup">
                <div className="fieldGroupHeader">
                  <span className="fieldGroupLabel">Bill-to (locked to ESTES002)</span>
                  <StatusBadge tone="green">{estesShipment.billToAccount || "ESTES002"}</StatusBadge>
                </div>
                <div className="formGrid">
                  <label className="field"><span>Bill-to Account</span><input value={estesShipment.billToAccount} onChange={(event) => updateEstesField("billToAccount", event.target.value)} /></label>
                  <label className="field wide"><span>Bill-to Name</span><input value={estesShipment.billName} onChange={(event) => updateEstesField("billName", event.target.value)} /></label>
                </div>
              </div>

              <div className="fieldGroup">
                <div className="fieldGroupHeader">
                  <span className="fieldGroupLabel">Shipper</span>
                  <span className="fieldGroupHint">
                    {estesShipment.shipState && !["AZ", "CA"].includes(estesShipment.shipState.toUpperCase())
                      ? "Outside AZ/CA — may rewrite to PHX forwarder"
                      : "AZ/CA direct"}
                  </span>
                </div>
                <div className="formGrid">
                  <label className="field wide"><span>Shipper Name</span><input value={estesShipment.shipName} onChange={(event) => updateEstesField("shipName", event.target.value)} /></label>
                  <label className="field"><span>City</span><input value={estesShipment.shipCity} onChange={(event) => updateEstesField("shipCity", event.target.value)} /></label>
                  <label className="field"><span>State</span><input value={estesShipment.shipState} onChange={(event) => updateEstesField("shipState", event.target.value)} /></label>
                  <label className="field"><span>Zip</span><input value={estesShipment.shipZip} onChange={(event) => updateEstesField("shipZip", event.target.value)} /></label>
                </div>
              </div>

              <div className="fieldGroup">
                <div className="fieldGroupHeader">
                  <span className="fieldGroupLabel">Consignee</span>
                  <span className="fieldGroupHint">
                    {estesShipment.consState && !["AZ", "CA"].includes(estesShipment.consState.toUpperCase())
                      ? "Outside AZ/CA — needs forwarder dock"
                      : "AZ/CA direct"}
                  </span>
                </div>
                <div className="formGrid">
                  <label className="field wide"><span>Consignee Name</span><input value={estesShipment.consName} onChange={(event) => updateEstesField("consName", event.target.value)} /></label>
                  <label className="field"><span>City</span><input value={estesShipment.consCity} onChange={(event) => updateEstesField("consCity", event.target.value)} /></label>
                  <label className="field"><span>State</span><input value={estesShipment.consState} onChange={(event) => updateEstesField("consState", event.target.value)} /></label>
                  <label className="field"><span>Zip</span><input value={estesShipment.consZip} onChange={(event) => updateEstesField("consZip", event.target.value)} /></label>
                </div>
                <div className="quickDocks">
                  <div className="smallLabel">Apply forwarder dock to consignee</div>
                  <div className="dockButtons">
                    {(estesForwarders?.quickMenu ?? []).map((entry) => (
                      <button type="button" key={entry.key} onClick={() => void applyEstesDock(entry.key, "consignee")} disabled={!adminSessionReady}>
                        {entry.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="fieldGroup">
                <div className="fieldGroupHeader">
                  <span className="fieldGroupLabel">Routing & Dates</span>
                  <span className="fieldGroupHint">DELIVERY ALERT fields trigger recovery shipper rewrite</span>
                </div>
                <div className="formGrid">
                  <label className="field"><span>Pickup Date (MM/DD/YYYY)</span><input value={estesShipment.shipDate} onChange={(event) => updateEstesField("shipDate", event.target.value)} placeholder="e.g. 05/15/2026" /></label>
                  <label className="field"><span>HAWB</span><input value={estesShipment.hawb} onChange={(event) => updateEstesField("hawb", event.target.value)} /></label>
                  <label className="field"><span>Waybill / MAWB</span><input value={estesShipment.waybillNo} onChange={(event) => updateEstesField("waybillNo", event.target.value)} /></label>
                  <label className="field"><span>Recovery Carrier</span><input value={estesShipment.recoveryCarrier} onChange={(event) => updateEstesField("recoveryCarrier", event.target.value)} placeholder="e.g. Forward Air" /></label>
                  <label className="field"><span>Recovery MAWB</span><input value={estesShipment.recoveryMawb} onChange={(event) => updateEstesField("recoveryMawb", event.target.value)} placeholder="e.g. 950-2147892" /></label>
                  <label className="field"><span>Dedupe Key</span><input value={estesShipment.dedupeKey} onChange={(event) => updateEstesField("dedupeKey", event.target.value)} /></label>
                </div>
              </div>

              <div className="fieldGroup">
                <div className="fieldGroupHeader">
                  <span className="fieldGroupLabel">Email / Attachment Context</span>
                  <span className="fieldGroupHint">Used to detect ESTES002 + forwarder routing</span>
                </div>
                <div className="formGrid">
                  <label className="field wide"><span>Subject</span><input value={estesSubject} onChange={(event) => { setEstesSubject(event.target.value); setEstesResult(null); }} /></label>
                  <label className="field wide"><span>Body</span><textarea rows={5} value={estesBody} onChange={(event) => { setEstesBody(event.target.value); setEstesResult(null); }} /></label>
                  <label className="field"><span>Source</span><input value={estesSourceLabel} onChange={(event) => setEstesSourceLabel(event.target.value)} /></label>
                </div>
              </div>

              {estesStatus && <div className="inlineNote">{estesStatus}</div>}
              {estesError && <div className="inlineAlert">{estesError}</div>}
            </div>

            <aside className="previewPane" aria-label="ESTES002 normalization output">
              <PanelHeader title="Normalization Notes" action={estesResult ? `${estesResult.notes.length} note${estesResult.notes.length === 1 ? "" : "s"}` : "no run yet"} />
              <div className="previewBlock">
                <div className="smallLabel">Applied rules</div>
                {estesResult?.notes.length ? (
                  <ul className="ruleList">
                    {estesResult.notes.map((note, index) => (
                      <li key={`note-${index}`}>{note}</li>
                    ))}
                  </ul>
                ) : (
                  <pre>Press Normalize to apply ESTES002 rules and see which fields changed.</pre>
                )}
              </div>
              {estesResult?.attentionRequired?.length ? (
                <div className="previewBlock">
                  <div className="smallLabel">Attention required</div>
                  <ul className="ruleList warn">
                    {estesResult.attentionRequired.map((note, index) => (
                      <li key={`attn-${index}`}>{note}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <div className="schedulerPreview">
                <div className="smallLabel">Detected Routing</div>
                <PreviewRow label="Carrier" value={estesResult?.detectedRouting?.carrier ?? "—"} />
                <PreviewRow label="MAWB" value={estesResult?.detectedRouting?.mawb ?? "—"} />
                <PreviewRow label="Service area" value={estesResult ? (estesResult.inServiceArea ? "AZ/CA direct" : "out of area → PHX forwarder") : "—"} />
                <PreviewRow label="Mode" value={estesResult?.mode ?? estesMode} />
              </div>
            </aside>
          </div>
        </section>
        )}

        {activeSection === "automation-path" && (
        <div className="contentGrid" id="automation-path">
          <section className="panel workflowPanel">
            <PanelHeader title="Shipment Automation Path" action="Shadow flow" />
            <div className="routeMap">
              {routeSteps.map((step, index) => (
                <div className="routeStep" key={step.label}>
                  <div className={`routeNode ${step.tone}`}>
                    <Icon name={step.label} />
                  </div>
                  <div>
                    <div className="routeLabel">{step.label}</div>
                    <div className="routeState">{step.state}</div>
                  </div>
                  {index < routeSteps.length - 1 && <div className="routeConnector" aria-hidden="true" />}
                </div>
              ))}
            </div>
          </section>

          <aside className="panel gatesPanel">
            <PanelHeader title="Profile Gates" action="No live writes" />
            <div className="gateList">
              {gates.map((gate) => (
                <div className="gateRow" key={gate.label}>
                  <span>{gate.label}</span>
                  <StatusBadge tone={gate.tone}>{gate.value}</StatusBadge>
                </div>
              ))}
            </div>
            <div className="profileVars">
              {profileVars.map((profileVar) => (
                <div className="varRow" key={profileVar.name}>
                  <div>
                    <code>{profileVar.name}</code>
                    <span>{profileVar.source}</span>
                  </div>
                  <strong>{profileVar.value}</strong>
                  <StatusBadge tone={profileVar.tone}>{profileVar.state}</StatusBadge>
                </div>
              ))}
            </div>
          </aside>
        </div>
        )}

        {activeSection === "events" && (
        <section className="tableBand" id="events">
          <div className="tableToolbar">
            <PanelHeader title="Recent Worker Events" action={status ? `${status.recentEvents.length} live` : "loading…"} />
          </div>
          <div className="tableWrap">
            {status && status.recentEvents.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Event</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {status.recentEvents.map((event, index) => (
                    <tr key={`${event.createdAt}-${index}`}>
                      <td>{formatDateTime(event.createdAt)}</td>
                      <td><code>{event.eventType}</code></td>
                      <td><StatusBadge tone={toneForStatus(event.status)}>{event.status}</StatusBadge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="inlineNote">{status ? "No worker events yet — the live feed will populate as Otto processes shipments." : "Loading worker events…"}</div>
            )}
          </div>
        </section>
        )}

        {activeSection === "setup" && (
        <section className="tableBand setupSection" id="setup" aria-label="Setup and operator notes">
          <PanelHeader title="Setup" action={bootConfig?.supabase.keySource === "secret_store" ? "Secret Store bindings active" : "review bindings"} />
          <div className="setupGrid">
            <article className="setupCard">
              <div className="setupCardHead">
                <h3>1 · Supabase publishable key</h3>
                <StatusBadge tone={bootConfig?.supabase.configured ? "green" : "amber"}>
                  {bootConfig?.supabase.keySource === "secret_store"
                    ? "Secret Store bound"
                    : bootConfig?.supabase.keySource === "env_var"
                    ? "env var bound"
                    : "not bound"}
                </StatusBadge>
              </div>
              <p>The portal fetches <code>SUPABASE_PUBLISHABLE_KEY</code> from the Otto worker at runtime. The key lives in Cloudflare Secret Store — never in source, never in the Pages build, never in a <code>VITE_*</code> env var.</p>
              <pre className="setupCode">{`# Copy the anon (publishable) key from
# Supabase dashboard -> Project Settings -> API
wrangler secrets-store secret create \\
  --store-id a8a96e35a308490ca37de879b2195232 \\
  --name SUPABASE_PUBLISHABLE_KEY \\
  --value 'PASTE_KEY_HERE'
cd hassett-keyme-ingest && npx wrangler deploy`}</pre>
            </article>

            <article className="setupCard">
              <div className="setupCardHead">
                <h3>2 · Outlook ingest token</h3>
                <StatusBadge tone={status?.credentialPosture.httpIngestTokenConfigured ? "green" : "amber"}>
                  {status?.credentialPosture.httpIngestTokenConfigured ? "bound" : "missing"}
                </StatusBadge>
              </div>
              <p><code>OTTO_HTTP_INGEST_TOKEN</code> is a shared random string the LDT tool uses to sign each emailed alert it forwards to Otto. Generate it once, bind it to the worker, save it on the workstation.</p>
              <pre className="setupCode">{`# PowerShell — emit a 32-byte base64 token
[Convert]::ToBase64String((1..32 | %{ Get-Random -Maximum 256 } | %{ [byte]$_ }))

# Bind on the worker
wrangler secret put OTTO_HTTP_INGEST_TOKEN
cd hassett-keyme-ingest && npx wrangler deploy`}</pre>
              <p className="setupHint">Anyone with this token can POST emails into Otto — treat it like a password.</p>
            </article>

            <article className="setupCard">
              <div className="setupCardHead">
                <h3>3 · LDT workstation</h3>
                <StatusBadge tone="slate">per-operator</StatusBadge>
              </div>
              <p>Store the ingest token in Windows Credential Manager (never <code>config.json</code>):</p>
              <pre className="setupCode">{`python -c "from app.secrets_store import set_otto_ingest_token; set_otto_ingest_token('PASTE_TOKEN')"`}</pre>
              <p>Then flip the bridge on:</p>
              <pre className="setupCode">{`{
  "otto_ingest_enabled": true,
  "otto_ingest_dry_run": false,
  "otto_ingest_base_url": "https://hassett-keyme-ingest.deadsetgg.workers.dev"
}`}</pre>
            </article>

            <article className="setupCard">
              <div className="setupCardHead">
                <h3>4 · Verify the bridge</h3>
                <StatusBadge tone={status?.gates.httpIngestEnabled ? "green" : "slate"}>
                  HTTP ingest {status?.gates.httpIngestEnabled ? "on" : "off"}
                </StatusBadge>
              </div>
              <p>Import an EFW alert from the LDT tool. The import notes will include one of:</p>
              <ul className="setupList">
                <li><code>otto: pass-through accepted</code> — Otto queued the email</li>
                <li><code>otto: pass-through skipped (disabled)</code> — bridge is off in config</li>
                <li><code>otto: pass-through skipped (otto_ingest_dry_run)</code> — dry-run mode, no network call</li>
                <li><code>otto: pass-through failed (…)</code> — see error detail</li>
              </ul>
            </article>
          </div>
        </section>
        )}

        {activeSection === "evidence" && (
        <section className="tableBand" id="evidence">
          <PanelHeader title="Evidence Pipeline" action="KeyMe profile" />
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Record</th>
                  <th>Confidence</th>
                  <th>Output</th>
                  <th>Gate</th>
                </tr>
              </thead>
              <tbody>
                {evidenceRows.map((row) => (
                  <tr key={`${row.source}-${row.record}`}>
                    <td>{row.source}</td>
                    <td>{row.record}</td>
                    <td>{row.confidence}</td>
                    <td>{row.output}</td>
                    <td>{row.gate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        )}
      </main>
    </div>
  );
}

function CustomerWorkspacePanel({ workspace }: { workspace: CustomerWorkspace }) {
  return (
    <div className="customerWorkspace">
      <div className="workspaceSummary">
        <div>
          <div className="smallLabel">{workspace.customerCode}</div>
          <h2>{workspace.customerName}</h2>
        </div>
        <StatusBadge tone={workspace.customerCode === "HASSE001" ? "green" : "slate"}>{workspace.focus}</StatusBadge>
      </div>
      <div className="workspaceColumns">
        <div className="workspaceColumn">
          <div className="columnTitle">Documents</div>
          {workspace.documents.map((item) => (
            <div className="workspaceRow" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.count}</strong>
              <StatusBadge tone={item.tone}>{item.state}</StatusBadge>
            </div>
          ))}
        </div>
        <div className="workspaceColumn">
          <div className="columnTitle">Shipment Types</div>
          {workspace.shipmentTypes.map((item) => (
            <div className="workspaceRow shipmentTypeRow" key={`${item.label}-${item.action}`}>
              <span>{item.label}</span>
              <strong>{item.action}</strong>
              <em>{item.model}</em>
              <StatusBadge tone={item.tone}>{item.state}</StatusBadge>
            </div>
          ))}
        </div>
        <div className="workspaceColumn">
          <div className="columnTitle">Knowledge Base</div>
          {workspace.knowledgeItems.map((item) => (
            <div className="workspaceRow" key={item.label}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
              <StatusBadge tone={item.tone}>rule</StatusBadge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SourceReviewPanel({ review }: { review: SourceReviewResponse }) {
  const tone: StatusTone = review.checks.some((check) => check.severity === "critical")
    ? "red"
    : review.checks.some((check) => check.severity === "warn")
      ? "amber"
      : "green";
  return (
    <div className="sourceReviewPanel">
      <div className="sourceReviewHeader">
        <div>
          <div className="smallLabel">Source vs staged request</div>
          <strong>{review.row.subject}</strong>
        </div>
        <StatusBadge tone={tone}>{review.ok ? "usable" : "blocked"}</StatusBadge>
      </div>
      <div className="sourceReviewGrid">
        <details className="previewBlock" open>
          <summary className="smallLabel">Stored email body preview</summary>
          <pre>{review.sourceEvidence.emailBodyPreview || "(no stored body preview)"}</pre>
        </details>
        <details className="previewBlock" open>
          <summary className="smallLabel">Staged normalized shape</summary>
          <table className="schedulerPreviewTable">
            <tbody>
              {Object.entries(review.staged.summary).map(([key, value]) => (
                <tr key={key}>
                  <td><code>{key}</code></td>
                  <td className="schedulerPreviewValue">{value || <em className="schedulerPreviewBlank">(blank)</em>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </div>
      <div className={review.sourceEvidence.attachmentState === "not_captured" ? "inlineAlert" : "inlineNote"}>
        {review.sourceEvidence.attachmentNote}
      </div>
      <div className="sourceChecks">
        {review.checks.map((check, index) => (
          <div className={`sourceCheck sourceCheck--${check.severity}`} key={`${check.area}-${index}`}>
            <strong>{check.area}</strong>
            <span>{check.message}</span>
            <small>{check.source}{check.evidence ? ` | ${check.evidence}` : ""}</small>
          </div>
        ))}
      </div>
      <details className="previewBlock">
        <summary className="smallLabel">Training JSON for agent-level examples</summary>
        <pre>{JSON.stringify(review.trainingExample, null, 2)}</pre>
      </details>
    </div>
  );
}

function NotificationPreviewTable({ preview }: { preview: OttoNotificationPreview }) {
  return (
    <div className="notificationPreview">
      <div className="notificationMeta">
        <span>Subject: <strong>{preview.subject}</strong></span>
        <span>From: {preview.from}</span>
        <span>Window: {formatDateTime(preview.windowStart)} - {formatDateTime(preview.windowEnd)}</span>
        <span>To: {preview.to.join(", ") || "(none)"}</span>
        <span>CC: {preview.cc.join(", ") || "(none)"}</span>
        <StatusBadge tone={preview.sendConfigured && preview.resendConfigured ? "green" : "amber"}>
          {preview.sendConfigured && preview.resendConfigured ? "Resend ready" : "Resend gated"}
        </StatusBadge>
      </div>
      <table className="schedulerPreviewTable notificationRows">
        <thead>
          <tr>
            <th>Account</th>
            <th>Action</th>
            <th>Reference</th>
            <th>Site</th>
            <th>FACTS</th>
            <th>Scheduler</th>
            <th>Pending</th>
          </tr>
        </thead>
        <tbody>
          {preview.rows.length ? preview.rows.map((row) => (
            <tr key={row.dedupeKey}>
              <td>{row.account}</td>
              <td>{row.action}</td>
              <td><code>{row.referenceNumber}</code></td>
              <td>{row.siteName}<br /><small>{row.city}, {row.state}</small></td>
              <td>{row.factsTraceUrl ? <a href={row.factsTraceUrl} target="_blank" rel="noreferrer">{row.factsTrackingNumber}</a> : "Pending"}</td>
              <td>{row.schedulerAction}</td>
              <td>{row.pendingAction}</td>
            </tr>
          )) : (
            <tr><td colSpan={7}><em>No matching Otto rows.</em></td></tr>
          )}
        </tbody>
      </table>
      <details className="previewBlock">
        <summary className="smallLabel">HTML email body</summary>
        <pre>{preview.html}</pre>
      </details>
    </div>
  );
}

function renderEvidence(evidence: string): ReactNode {
  const text = String(evidence ?? "");
  const xmlMatch = text.match(/response_xml=([\s\S]*)$/);
  if (xmlMatch) {
    const before = text.slice(0, xmlMatch.index).replace(/\s*\|\s*$/, "");
    const responseXml = xmlMatch[1] ?? "";
    return (
      <>
        {before && <code className="aiReviewEvidence">{before}</code>}
        <details className="aiReviewEvidenceXml">
          <summary>view response_xml ({responseXml.length} chars)</summary>
          <pre>{responseXml}</pre>
        </details>
      </>
    );
  }
  return <code className="aiReviewEvidence">{text}</code>;
}

function AiReviewPanel({ review }: { review: AiReviewResponse }) {
  const tone: StatusTone = review.findings.some((f) => f.severity === "critical")
    ? "red"
    : review.findings.some((f) => f.severity === "warn")
      ? "amber"
      : "green";

  const sourcesByKey = useMemo(() => {
    const map = new Map<string, AiReviewSource>();
    for (const source of review.sources) map.set(source.key, source);
    return map;
  }, [review.sources]);

  const findingsBySource = useMemo(() => {
    const map = new Map<string, AiReviewFinding[]>();
    for (const finding of review.findings) {
      const list = map.get(finding.source) ?? [];
      list.push(finding);
      map.set(finding.source, list);
    }
    return map;
  }, [review.findings]);

  return (
    <div className={`aiReviewPanel aiReviewPanel--${tone}`}>
      <div className="aiReviewHead">
        <div>
          <span className="smallLabel">AI Review · {review.mode}</span>
          <div className="aiReviewTimestamp">checked {formatTime(review.generatedAt)}</div>
        </div>
        <StatusBadge tone={tone}>{review.summary}</StatusBadge>
      </div>

      <div className="aiReviewSources" aria-label="Live evidence sources">
        <div className="smallLabel">Live evidence sources</div>
        <div className="aiReviewSourceGrid">
          {review.sources.map((source) => (
            <div className={`aiReviewSource aiReviewSource--${source.online ? "online" : "offline"}`} key={source.key}>
              <div className="aiReviewSourceHead">
                <span className={`dot ${source.online ? "green" : "slate"}`} aria-hidden="true" />
                <strong>{source.label}</strong>
                <span className="aiReviewSourceTag">{findingsBySource.get(source.key)?.length ?? 0} cited</span>
              </div>
              <p>{source.detail}</p>
              {source.lastSignalAt && (
                <small>last signal: {formatDateTime(source.lastSignalAt)}</small>
              )}
            </div>
          ))}
        </div>
      </div>

      {review.findings.length === 0 ? (
        <div className="aiReviewEmpty">No issues detected.</div>
      ) : (
        <ul className="aiReviewList">
          {review.findings.map((finding, index) => {
            const source = sourcesByKey.get(finding.source);
            return (
              <li key={`${finding.source}-${index}`} className={`aiReviewItem aiReviewItem--${finding.severity}`}>
                <span className={`aiReviewBadge aiReviewBadge--${finding.severity}`}>{finding.severity}</span>
                <span className="aiReviewArea">{finding.area}</span>
                <div className="aiReviewContent">
                  <div className="aiReviewMessage">{finding.message}</div>
                  <div className="aiReviewMeta">
                    <span className="aiReviewSourceChip" title={source?.detail ?? finding.source}>
                      source: {source?.label ?? finding.source}
                    </span>
                    {finding.evidence && renderEvidence(finding.evidence)}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ReadinessContractPanel({
  sourceReview,
  preview,
  aiReview,
  warningsAcknowledged,
  onAcknowledgeWarnings
}: {
  sourceReview: SourceReviewResponse | null;
  preview: ShipmentPreviewResponse | null;
  aiReview: AiReviewResponse | null;
  warningsAcknowledged: boolean;
  onAcknowledgeWarnings: (value: boolean) => void;
}) {
  const readiness = aiReview?.readiness;
  const sourceCritical = sourceReview?.checks.some((check) => check.severity === "critical") ?? false;
  const sourceWarn = sourceReview?.checks.some((check) => check.severity === "warn") ?? false;
  const steps: Array<{ label: string; detail: string; tone: StatusTone }> = [
    {
      label: "Source Review",
      detail: sourceReview ? (sourceCritical ? "critical source blockers" : sourceWarn ? "warnings present" : "source checked") : "not loaded",
      tone: sourceReview ? (sourceCritical ? "red" : sourceWarn ? "amber" : "green") : "slate"
    },
    {
      label: "FACTS XML Preview",
      detail: preview ? (preview.ok ? "dry-run preview clean" : `${preview.validationErrors.length} validation issue(s)`) : "not generated",
      tone: preview ? (preview.ok ? "green" : "red") : "slate"
    },
    {
      label: "Duplicate Check",
      detail: readiness ? readiness.duplicateCheckStatus : "not checked",
      tone: readiness ? (readiness.duplicateCheckStatus === "clear" ? "green" : "red") : "slate"
    },
    {
      label: "Operator Approval",
      detail: readiness ? (readiness.submitEligible ? "snapshot eligible" : readiness.blockers.join(" | ")) : "run AI Review",
      tone: readiness ? (readiness.submitEligible ? "green" : "red") : "slate"
    }
  ];

  return (
    <div className="readinessContract">
      <div className="readinessHeader">
        <div>
          <span className="smallLabel">Submit SOP</span>
          <strong>Backend readiness contract</strong>
        </div>
        <StatusBadge tone={readiness?.submitEligible ? "green" : "amber"}>
          {readiness?.submitEligible ? "snapshot ready" : "submit locked"}
        </StatusBadge>
      </div>
      <div className="readinessSteps">
        {steps.map((step, index) => (
          <div className="readinessStep" key={step.label}>
            <span>{index + 1}</span>
            <div>
              <strong>{step.label}</strong>
              <small>{step.detail}</small>
            </div>
            <StatusBadge tone={step.tone}>{step.tone}</StatusBadge>
          </div>
        ))}
      </div>
      {readiness && readiness.warnCount > 0 && readiness.submitEligible && (
        <label className="checkboxRow readinessAck">
          <input
            type="checkbox"
            checked={warningsAcknowledged}
            onChange={(event) => onAcknowledgeWarnings(event.target.checked)}
          />
          <span>Acknowledge {readiness.warnCount} warning(s) from the latest readiness snapshot</span>
        </label>
      )}
      {readiness && (
        <div className="readinessMeta">
          <span>Snapshot {readiness.id.slice(0, 8)}</span>
          <span>Expires {formatDateTime(readiness.expiresAt)}</span>
          <span>XML {readiness.requestXmlHash}</span>
        </div>
      )}
    </div>
  );
}

function SchedulingBanner({
  confirmed,
  onToggle,
  weekEndingDate,
  suggestedServiceDate,
  requestedCompletionDate,
  schedulingContact
}: {
  confirmed: boolean;
  onToggle: (value: boolean) => void;
  weekEndingDate: string;
  suggestedServiceDate: string;
  requestedCompletionDate?: string;
  schedulingContact: string;
}) {
  const wePretty = formatScheduleDateShort(weekEndingDate);
  const servicePretty = formatScheduleDateShort(suggestedServiceDate);
  const requestedPretty = formatScheduleDateShort(requestedCompletionDate ?? "");
  let preview: string;
  if (confirmed) {
    preview = servicePretty
      ? `Scheduler will write DATE = ${servicePretty} and CONFIRMED = CONFIRMED.`
      : "Scheduler will write the Service Date once confirmed.";
  } else if (wePretty) {
    const contactLine = schedulingContact ? ` Contact: ${schedulingContact} for scheduling.` : "";
    preview = `Scheduler will write DATE = WE ${wePretty} and prepend "TBS - schedule & complete by WE ${wePretty}" to Deployment Notes.${contactLine}`;
  } else {
    preview = "No Week Ending date set — DATE will fall back to Service Date / Market ETA / Earliest.";
  }
  return (
    <div className={`schedulingBanner ${confirmed ? "schedulingBanner--confirmed" : "schedulingBanner--tbs"}`}>
      <label className="checkboxRow">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => onToggle(event.target.checked)}
        />
        <span>{confirmed ? "Service date confirmed with site" : "Service date not confirmed; schedule by Week Ending"}</span>
      </label>
      {requestedPretty && (
        <p><strong>Requested completion:</strong> {requestedPretty} (source email asks to complete on this date).</p>
      )}
      <p>{preview}</p>
    </div>
  );
}

function editorFamilyLabel(job: ReviewShipmentJob): string {
  if (job.source === "hassett-special-action") {
    const family = job.family ? job.family.charAt(0).toUpperCase() + job.family.slice(1) : "Special Action";
    return `${family} — Selected Action`;
  }
  if (job.source === "hassett-keyme") return "KeyMe — Selected Action";
  return "Selected Action";
}

function pickSchedulingContactDisplay(job: ReviewShipmentJob): string {
  const name = (job.contactName || job.storeContact || "").trim();
  const phone = (job.contactPhone || job.storePhone || "").trim();
  const email = (job.contactEmail || "").trim();
  return [name, phone, email].filter(Boolean).join(" / ");
}

function prettyXml(xml: string): string {
  const trimmed = String(xml ?? "").trim();
  if (!trimmed) return "";
  return trimmed.replace(/></g, ">\n<");
}

function formatScheduleDateShort(value: string): string {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "";
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${Number(iso[2])}/${Number(iso[3])}`;
  const us = trimmed.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/);
  if (us) return `${Number(us[1])}/${Number(us[2])}`;
  return trimmed;
}

function SummaryItem({ label, value, tone }: { label: string; value: string; tone: StatusTone }) {
  return (
    <div className="summaryItem">
      <span>{label}</span>
      <StatusBadge tone={tone}>{value}</StatusBadge>
    </div>
  );
}

function TrialItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="trialItem">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="previewRow">
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function onOff(value: boolean) {
  return value ? "on" : "off";
}

function formatTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function PanelHeader({ title, action }: { title: string; action: string }) {
  return (
    <div className="panelHeader">
      <h2>{title}</h2>
      <span>{action}</span>
    </div>
  );
}

function StatusBadge({ tone, children }: { tone: StatusTone; children: ReactNode }) {
  return <span className={`statusBadge ${tone}`}>{children}</span>;
}

function StatusDot({ tone }: { tone: StatusTone }) {
  return <span className={`statusDot ${tone}`} aria-hidden="true" />;
}

function OperatorToastStack({ alerts, onDismiss }: { alerts: OttoOperatorAlert[]; onDismiss: (id: string) => void }) {
  if (alerts.length === 0) return null;
  return (
    <div className="operatorToastStack" aria-live="polite" aria-label="Operator notifications">
      {alerts.map((alert) => (
        <button type="button" className={`operatorToast operatorToast--${alert.severity}`} key={alert.id} onClick={() => onDismiss(alert.id)}>
          <strong>{alert.title}</strong>
          <span>{alert.body}</span>
        </button>
      ))}
    </div>
  );
}

function toneForStatus(status: string): StatusTone {
  if (["complete", "locked"].includes(status)) return "green";
  if (["review", "shadow"].includes(status)) return "blue";
  if (status === "off") return "slate";
  return "amber";
}

function normalizeReviewJob(value: unknown): ReviewShipmentJob {
  const raw = value && typeof value === "object" ? value as Partial<ReviewShipmentJob> & Record<string, unknown> : {};
  const source = raw.source === "hassett-special-action" ? "hassett-special-action" : "hassett-keyme";
  const scheduleRequirement = isRecord(raw.scheduleRequirement) ? raw.scheduleRequirement : {};
  const poNumber = String(raw.poNumber ?? "");
  const externalId = String(raw.externalId ?? raw.keyMeId ?? "");
  const action = String(raw.task ?? raw.action ?? "");
  const suggestedServiceDate = String(raw.suggestedServiceDate
    ?? raw.scheduledDate
    ?? raw.requestedCompletionDate
    ?? scheduleRequirement.earliestServiceDate
    ?? (source === "hassett-special-action" ? "" : demoReviewJob.suggestedServiceDate));
  const weekEndingDate = String(raw.weekEndingDate
    ?? (source === "hassett-special-action" ? weekEndingFriday(suggestedServiceDate) : demoReviewJob.weekEndingDate));
  const serialNumber = String(raw.serialNumber
    ?? raw.machineId
    ?? (source === "hassett-special-action" ? "PENDING" : demoReviewJob.serialNumber));
  return {
    ...demoReviewJob,
    ...raw,
    source,
    customerCode: String(raw.customerCode ?? "HASSE001"),
    poNumber,
    workOrderId: String(raw.workOrderId ?? poNumber),
    keyMeId: source === "hassett-special-action" ? externalId : String(raw.keyMeId ?? ""),
    task: action,
    storeContact: String(raw.storeContact ?? raw.contactName ?? ""),
    storePhone: String(raw.storePhone ?? raw.contactPhone ?? ""),
    jobNotes: String(raw.jobNotes ?? raw.freightDescription ?? ""),
    rawSubject: String(raw.rawSubject ?? raw.subject ?? ""),
    rawFrom: String(raw.rawFrom ?? raw.requestedBy ?? ""),
    suggestedServiceDate,
    weekEndingDate,
    scheduledDateConfirmed: raw.scheduledDateConfirmed === true,
    serialNumber,
    confidence: Number(raw.confidence ?? demoReviewJob.confidence),
    missingRequired: Array.isArray(raw.missingRequired) ? raw.missingRequired.map(String) : [],
    family: typeof raw.family === "string" ? raw.family : undefined,
    action: typeof raw.action === "string" ? raw.action : undefined,
    externalId,
    subjectLocationCode: typeof raw.subjectLocationCode === "string" ? raw.subjectLocationCode : undefined,
    machineId: typeof raw.machineId === "string" ? raw.machineId : undefined,
    freightDescription: typeof raw.freightDescription === "string" ? raw.freightDescription : undefined,
    referenceHints: Array.isArray(raw.referenceHints) ? raw.referenceHints.filter(isRecord) : undefined,
    productGate: isRecord(raw.productGate) ? raw.productGate : undefined,
    routing: isRecord(raw.routing) ? raw.routing : undefined
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function weekEndingFriday(value: string): string {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return "";
  const day = date.getUTCDay();
  const daysUntilFriday = (5 - day + 7) % 7;
  date.setUTCDate(date.getUTCDate() + daysUntilFriday);
  return date.toISOString().slice(0, 10);
}

function mergeAlerts(next: OttoOperatorAlert[], current: OttoOperatorAlert[]): OttoOperatorAlert[] {
  const seen = new Set<string>();
  const merged: OttoOperatorAlert[] = [];
  for (const alert of [...next, ...current]) {
    if (seen.has(alert.id)) continue;
    seen.add(alert.id);
    merged.push(alert);
  }
  return merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function playOperatorTone(alert: OttoOperatorAlert): void {
  try {
    const AudioContextCtor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;
    const ctx = new AudioContextCtor();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    const success = alert.alertType === "facts_success" || alert.severity === "success";
    oscillator.type = "sine";
    oscillator.frequency.value = success ? 880 : 330;
    gain.gain.value = success ? 0.045 : 0.025;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + (success ? 0.14 : 0.22));
    oscillator.onended = () => void ctx.close();
  } catch {
    // Browser autoplay policy may block audio until the operator has interacted.
  }
}

async function callOttoAdmin<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${ottoAdminBaseUrl}${path}`, {
    ...init,
    cache: "no-store",
    headers: {
      authorization: `Bearer ${token.trim()}`,
      ...(init.body ? { "content-type": "application/json" } : {})
    }
  });
  const text = await response.text();
  const payload = text ? safeJsonParse(text) : null;
  if (!response.ok) {
    const message = payload && typeof payload === "object" && "error" in payload
      ? String((payload as { error: unknown }).error)
      : text || `Worker admin HTTP ${response.status}`;
    throw new Error(message);
  }
  return payload as T;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function BootSplash({ subtitle }: { subtitle: string }) {
  return (
    <div className="bootShell">
      <div className="bootCard">
        <div className="bootBrand">
          <div className="brandMark" aria-hidden="true">O</div>
          <div>
            <div className="brandName">Otto</div>
            <div className="brandUrl">otto.ldtws.dev</div>
          </div>
        </div>
        <div className="bootSpinner" aria-hidden="true" />
        <div className="bootSubtitle">{subtitle}</div>
      </div>
    </div>
  );
}

function BootGate({ title, subtitle, body }: { title: string; subtitle: string; body: ReactNode }) {
  return (
    <div className="bootShell">
      <div className="bootCard bootCard--wide">
        <div className="bootBrand">
          <div className="brandMark" aria-hidden="true">O</div>
          <div>
            <div className="brandName">Otto</div>
            <div className="brandUrl">otto.ldtws.dev</div>
          </div>
        </div>
        <div className="bootHeading">
          <h1 className="bootTitle">{title}</h1>
          <p className="bootSubtitle">{subtitle}</p>
        </div>
        <div className="bootBody">{body}</div>
      </div>
    </div>
  );
}

interface SignInScreenProps {
  bootConfig: OttoPublicConfig | null;
  authEmail: string;
  setAuthEmail: (value: string) => void;
  authPassword: string;
  setAuthPassword: (value: string) => void;
  authMode: "magic" | "password";
  setAuthMode: (mode: "magic" | "password") => void;
  authLoading: boolean;
  authStatus: string;
  authError: string;
  onSignInWithPassword: () => void;
  onSendMagicLink: () => void;
  onResetPassword: () => void;
}

function SignInScreen({
  bootConfig,
  authEmail,
  setAuthEmail,
  authPassword,
  setAuthPassword,
  authMode,
  setAuthMode,
  authLoading,
  authStatus,
  authError,
  onSignInWithPassword,
  onSendMagicLink,
  onResetPassword
}: SignInScreenProps) {
  const allowedDomains = bootConfig?.adminAuth.allowedEmailDomains ?? [];
  return (
    <div className="bootShell">
      <div className="bootCard bootCard--wide signInScreenCard">
        <div className="bootBrand">
          <div className="brandMark" aria-hidden="true">O</div>
          <div>
            <div className="brandName">Otto</div>
            <div className="brandUrl">otto.ldtws.dev</div>
          </div>
        </div>
        <div className="bootHeading">
          <h1 className="bootTitle">Welcome back</h1>
          <p className="bootSubtitle">
            {allowedDomains.length > 0
              ? `Sign in with your ${allowedDomains.map((domain) => `@${domain}`).join(" or ")} account to access the Otto admin console.`
              : "Use the email your administrator added to the Otto allowlist."}
          </p>
        </div>

        <div className="segmented authModeSwitch" aria-label="Login method">
          <button type="button" className={authMode === "password" ? "selected" : ""} onClick={() => setAuthMode("password")}>
            Password
          </button>
          <button type="button" className={authMode === "magic" ? "selected" : ""} onClick={() => setAuthMode("magic")}>
            Magic Link
          </button>
        </div>

        <label className="field wide">
          <span>Email</span>
          <input
            type="email"
            value={authEmail}
            onChange={(event) => setAuthEmail(event.target.value)}
            placeholder="name@linedrivetrucking.com"
            disabled={authLoading}
            autoComplete="username"
            autoFocus
          />
        </label>

        {authMode === "password" ? (
          <>
            <label className="field wide">
              <span>Password</span>
              <input
                type="password"
                value={authPassword}
                onChange={(event) => setAuthPassword(event.target.value)}
                placeholder="••••••••"
                disabled={authLoading}
                autoComplete="current-password"
                onKeyDown={(event) => { if (event.key === "Enter") onSignInWithPassword(); }}
              />
            </label>
            <div className="signInActions">
              <button type="button" className="primaryButton" onClick={onSignInWithPassword} disabled={authLoading || !authEmail.trim() || !authPassword}>
                {authLoading ? "Signing in…" : "Sign In"}
              </button>
              <button type="button" className="textLink" onClick={onResetPassword} disabled={authLoading || !authEmail.trim()}>
                Reset password
              </button>
            </div>
          </>
        ) : (
          <div className="signInActions">
            <button type="button" className="primaryButton" onClick={onSendMagicLink} disabled={authLoading || !authEmail.trim()}>
              {authLoading ? "Sending…" : "Email Magic Link"}
            </button>
          </div>
        )}

        {authStatus && <div className="inlineNote">{authStatus}</div>}
        {authError && <div className="inlineAlert">{authError}</div>}

        <div className="bootFooter">
          <span className="dot green" /> Otto worker connected · {bootConfig?.supabase.keySource === "secret_store" ? "Secret Store key" : "Worker key"}
        </div>
      </div>
    </div>
  );
}

function NavIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    dashboard: (
      <>
        <rect x="3" y="3" width="7" height="9" rx="1.5" />
        <rect x="14" y="3" width="7" height="5" rx="1.5" />
        <rect x="14" y="12" width="7" height="9" rx="1.5" />
        <rect x="3" y="16" width="7" height="5" rx="1.5" />
      </>
    ),
    review: (
      <>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M8 8h8M8 12h8M8 16h5" />
      </>
    ),
    customers: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20c0-3 3-5 6-5s6 2 6 5" />
        <circle cx="17" cy="6" r="2" />
        <path d="M14 13c1-1 2-1.5 3-1.5 2.5 0 4 2 4 4" />
      </>
    ),
    estes: (
      <>
        <path d="M3 7h13l5 5-5 5H3z" />
        <path d="M7 12h6" />
      </>
    ),
    route: (
      <>
        <circle cx="6" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <path d="M6 8.5v3a3 3 0 0 0 3 3h6a3 3 0 0 1 3 3v-1" />
      </>
    ),
    events: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
    evidence: (
      <>
        <path d="M5 4h10l4 4v12H5z" />
        <path d="M15 4v4h4" />
        <path d="M8 13h8M8 17h5" />
      </>
    ),
    setup: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.4-2.4.8a7 7 0 0 0-2.1-1.2L14 3h-4l-.4 2.5a7 7 0 0 0-2.1 1.2l-2.4-.8-2 3.4 2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.4 2.4-.8a7 7 0 0 0 2.1 1.2L10 21h4l.4-2.5a7 7 0 0 0 2.1-1.2l2.4.8 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z" />
      </>
    )
  };
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {paths[name] ?? paths.dashboard}
    </svg>
  );
}

function Icon({ name }: { name: string }) {
  if (/AI|Routes|Customers/.test(name)) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3v18M4 8h16M6 16h12" />
        <circle cx="12" cy="8" r="3" />
      </svg>
    );
  }
  if (/D1|Ledger|Evidence|Deploy/.test(name)) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 7c0-2 14-2 14 0v10c0 2-14 2-14 0V7Z" />
        <path d="M5 12c0 2 14 2 14 0M5 17c0 2 14 2 14 0" />
      </svg>
    );
  }
  if (/FACTS|Scheduler|Queue|Review/.test(name)) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 5h12v14H6z" />
        <path d="M9 9h6M9 13h6M9 17h4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6h16M4 12h16M4 18h16" />
      <path d="M8 4v16M16 4v16" />
    </svg>
  );
}

export default App;
